/**
 * Site content — single source of truth for all public marketing copy.
 *
 * COMPLIANCE GUARDRAILS (enforced by tests in src/content/site.test.ts):
 *  - Skill-based only. NEVER lottery / raffle / draw / chance / lucky /
 *    "buy chances" language anywhere in this file.
 *  - "Guaranteed given away" / "won by skill" — NEVER "guaranteed you'll win".
 *  - Opt-in leads only; never "collect everyone's data".
 *  - No guaranteed sales / leads / viral reach.
 *
 * Copy written via marketing-skills (copywriting + CRO); structure per the
 * Enterprise-Gateway B2B pattern (ui-ux-pro-max).
 */
import type { IconName } from '../components/Icon';

export const BRAND = {
  name: 'My Investments — Corporate Partnerships',
  shortName: 'Corporate Partnerships',
  domain: 'corporate.my-investments.io',
};

export type Cta = { label: string; href: string };

// CTA library (vision §13) — only the conversion-relevant subset is surfaced.
export const CTAS = {
  becomePartner: { label: 'Become a Corporate Partner', href: '#sponsor-a-prize' } as Cta,
  sponsorPrize: { label: 'Sponsor a Prize', href: '#sponsor-a-prize' } as Cta,
  bookCall: { label: 'Book a Campaign Call', href: 'mailto:partnerships@my-investments.io?subject=Book%20a%20Campaign%20Call' } as Cta,
  requestPack: { label: 'Request a Partnership Pack', href: 'mailto:partnerships@my-investments.io?subject=Request%20a%20Partnership%20Pack' } as Cta,
};

export const HERO = {
  eyebrow: 'For brands, dealerships & retailers',
  headline: 'Turn your product into a guaranteed prize campaign.',
  // Compliance-safe: "given away to a winner decided by skill" — never "you'll win".
  // Benefit-led subcopy: lead with the outcome the sponsor buys (foot traffic,
  // consented leads, a brand moment), then name the mechanic.
  subcopy:
    'Put your product at the centre of a skill-based competition and turn it into measurable foot traffic, opt-in leads with recorded consent, and a winner story people share. We design, run and track the whole campaign — you co-brand and watch the results land.',
  primaryCta: CTAS.sponsorPrize,
  secondaryCta: CTAS.bookCall,
  trustLine:
    'Skill-based competitions on the My Investments Prizes platform — every winner is decided by published skill criteria.',
};

// Headline stats — framed as capabilities, NOT guaranteed outcomes.
export const HERO_STATS: { value: string; label: string }[] = [
  { value: 'Done-for-you', label: 'campaign design, build & winner content' },
  { value: 'Opt-in', label: 'leads with recorded, campaign-specific consent' },
  { value: 'Tracked', label: 'QR scans, clicks & entries on every asset' },
];

export type PartnershipType = {
  id: string;
  icon: IconName;
  title: string;
  blurb: string;
  example: string;
  /** v1-11-1: studio-generated card art (public/studio/...). */
  image: string;
};

export const PARTNERSHIP_TYPES: PartnershipType[] = [
  {
    id: 'prize-sponsor',
    image: '/studio/partnership/prize-sponsor.webp',
    icon: 'trophy',
    title: 'Prize Sponsor',
    blurb: 'Provide the full prize for a campaign and put your product at the centre of a public, skill-based competition.',
    example: 'A car dealership sponsors a demo vehicle as the prize.',
  },
  {
    id: 'co-sponsor',
    image: '/studio/partnership/co-sponsor.webp',
    icon: 'handshake',
    title: 'Co-Sponsor',
    blurb: 'Provide part of the prize value, a discount, or partial funding — we structure the rest of the campaign around it.',
    example: 'A dealership discounts the vehicle while My Investments funds the balance.',
  },
  {
    id: 'retail-voucher',
    image: '/studio/partnership/retail-voucher.webp',
    icon: 'voucher',
    title: 'Retail Voucher Sponsor',
    blurb: 'Offer voucher-based prizes that are easy to fulfil and carry strong, repeat brand value.',
    example: 'A retailer sponsors grocery vouchers across 12 months.',
  },
  {
    id: 'dealership-foot-traffic',
    image: '/studio/partnership/dealership-foot-traffic.webp',
    icon: 'storefront',
    title: 'Dealership Foot-Traffic Partner',
    blurb: 'Host the prize in-store and place QR codes on the product, desks, banners and your website to route real visits to your floor.',
    example: 'Visitors find the competition vehicle in the showroom and scan to enter.',
  },
  {
    id: 'advertising',
    image: '/studio/partnership/advertising.webp',
    icon: 'megaphone',
    title: 'Advertising Partner',
    blurb: 'Buy campaign visibility — sponsored placement, banners, newsletters, social features and in-app advertising.',
    example: 'A brand sponsors placement across active competition pages.',
  },
  {
    id: 'affiliate-service',
    image: '/studio/partnership/affiliate-service.webp',
    icon: 'link',
    title: 'Affiliate Service Partner',
    blurb: 'Qualify your active users for complimentary skill attempts as part of a promotional campaign.',
    example: 'A partner service offers its members a complimentary skill attempt.',
  },
  {
    id: 'creator',
    image: '/studio/partnership/creator.webp',
    icon: 'creator',
    title: 'Creator / Influencer Partner',
    blurb: 'Promote corporate-sponsored competitions through tracked links, QR codes, campaign briefs and social content.',
    example: 'A creator shares a tracked link to a sponsored competition.',
  },
];

export type Benefit = {
  icon: IconName;
  title: string;
  description: string;
};

export const BENEFITS: Benefit[] = [
  {
    icon: 'sparkles',
    title: 'A brand moment people want in on',
    description: 'Your product becomes the prize at the centre of a public, skill-based competition — a campaign people actively compete to win, not an ad they scroll past.',
  },
  {
    icon: 'leads',
    title: 'Opt-in leads, not a scraped list',
    description: 'Entrants choose to be contacted about your product, with recorded, campaign-specific consent. Every lead you receive is genuine, lawful, POPIA-aware interest.',
  },
  {
    icon: 'traffic',
    title: 'Real foot & website traffic',
    description: 'QR codes on the product, in-store and on banners route real visits to your showroom or floor; tracked links send qualified clicks to your site.',
  },
  {
    icon: 'qr',
    title: 'Attribution on every asset',
    description: 'Every scan, click and entry is tied back to your campaign, so you can see exactly which placement, channel and creative did the work.',
  },
  {
    icon: 'video',
    title: 'Winner content that does the selling',
    description: 'Prize handover videos and winner announcements give you ready-made social proof — real people, real reactions, your brand front and centre.',
  },
  {
    icon: 'chart',
    title: 'A report, not a promise',
    description: 'You get measurable numbers on scans, clicks, entries and opt-in leads at the end of every run — outcomes you can take to your board, not vanity metrics.',
  },
];

export type CampaignExample = {
  id: string;
  icon: IconName;
  /** v1-11-1: studio-generated hero art. */
  image: string;
  badge: string;
  name: string;
  partner: string;
  prize: string;
  // Compliance-safe: the prize is "given away to a winner decided by skill".
  summary: string;
  flow: string[];
  benefits: string[];
};

export const CAMPAIGN_EXAMPLES: CampaignExample[] = [
  {
    id: 'car-dealership-showroom',
    image: '/studio/campaign/car-dealership-showroom.webp',
    icon: 'car',
    badge: 'Dealership foot-traffic',
    name: 'Win a Vehicle — The Showroom Challenge',
    partner: 'Vehicle dealership',
    prize: 'A demo vehicle (or approved vehicle prize)',
    summary:
      'A co-branded competition where the vehicle is given away to the winner of a published skill challenge — driving showroom traffic and product attention.',
    flow: [
      'The dealership provides or discounts the prize vehicle.',
      'We create a co-branded competition page.',
      'QR codes go on the vehicle, showroom desk, website, social and banners.',
      'Visitors scan, land on the competition page and complete the skill challenge.',
      'Traffic is attributed to the dealership campaign.',
      'Opt-in leads can be routed to the dealership (with consent).',
      'The winner is verified and the prize handover is captured and published.',
    ],
    benefits: ['More showroom traffic', 'Product attention', 'Opt-in leads', 'Campaign reporting'],
  },
  {
    id: 'groceries',
    image: '/studio/campaign/groceries.webp',
    icon: 'cart',
    badge: 'Retail voucher',
    name: 'Win Groceries for a Year',
    partner: 'Grocery store',
    prize: 'Monthly grocery vouchers across 12 months',
    summary:
      'A retail voucher campaign with strong public appeal — vouchers are given away to a winner decided by skill, with monthly winner-content opportunities.',
    flow: [
      'A retail partner sponsors the grocery vouchers.',
      'We create the campaign page.',
      'Entrants take part through the skill-based flow.',
      'Campaign links and QR codes are distributed across partner channels.',
      'The winner receives monthly vouchers; campaign reporting is shared.',
    ],
    benefits: ['Strong public appeal', 'Repeat brand engagement', 'Easy fulfilment', 'Shareable story'],
  },
];

// "How it works" — reduces perceived complexity (CRO).
export const HOW_IT_WORKS_IMAGE = '/studio/campaign/how-it-works.webp';

export const HOW_IT_WORKS: { step: string; title: string; description: string }[] = [
  { step: '1', title: 'Apply', description: 'Tell us about your company, the product you want to sponsor and your campaign goals.' },
  { step: '2', title: 'We review & set up', description: 'Our team checks brand fit, prize details and feasibility, then builds a co-branded competition page.' },
  { step: '3', title: 'Launch & track', description: 'Place your QR codes and links; we attribute every scan, click and entry to your campaign.' },
  { step: '4', title: 'Winner & report', description: 'The winner is verified by skill, the handover is captured, and you receive a campaign report.' },
];

// FAQ — answers the real questions a prospective corporate partner asks
// before applying. Compliance-safe: skill-based only, consent-first, no
// guaranteed outcomes (enforced by src/content/site.test.ts).
export type FaqItem = { question: string; answer: string };

export const FAQ: FaqItem[] = [
  {
    question: 'What is a skill-based competition, and why use it for my brand?',
    answer:
      'Entrants win by completing a published skill task — the winner is decided solely by skill criteria, never by luck. That is what keeps the campaign compliant and brand-safe: it is a competition of merit, not a game of fortune. For your brand it means a credible, public moment built entirely around your product.',
  },
  {
    question: 'How does sponsoring a prize actually make me money?',
    answer:
      'Your product sits at the centre of a public campaign that we build and run for you. The return shows up as real showroom and foot traffic from QR codes, opt-in leads with recorded consent, brand exposure across competition pages and social, and shareable winner content. Every scan, click and entry is tracked, so you get a full report on what the campaign delivered.',
  },
  {
    question: 'What do I actually have to do to advertise it?',
    answer:
      'Very little. We design and run the entire campaign — the competition page, the creative, the content and the tracking. Your part is to co-brand it and host the QR code or competition link where your audience already is: on the product, in-store, on your website and across your social channels.',
  },
  {
    question: 'How are leads handled?',
    answer:
      'Carefully and lawfully. Participant details are only ever shared with you where that participant has given clear, recorded, campaign-specific consent to be contacted about your product. There is no blanket data harvesting — it is consent-first and POPIA-aware by design, so the leads you receive are genuine opt-in interest.',
  },
  {
    question: 'What does it cost, and how does funding work?',
    answer:
      "Funding is flexible and we share the risk with you — there's no hard lock-in. We'll fully fund the first few campaigns while we build trust together, at no prize cost to you. After that, every campaign is open to negotiation: some sponsored by you, some fully funded by us, whatever fits your goals and budget. No 'you're on your own after run one.' We agree the specifics with you up front — no surprises.",
  },
  {
    question: 'What is the timeline from applying to going live?',
    answer:
      'It is deliberately simple: you apply, we review and approve the fit, we put a short partnership agreement in place, we set up your co-branded competition, and then we launch. Most campaigns move from approval to live quickly once the prize and brand assets are confirmed.',
  },
  {
    question: 'Do I need a product to sponsor, and what do I need to start?',
    answer:
      'You need a product, voucher or experience worth showcasing as the prize, your co-branding assets (logo and brand guidelines), and a signed short-form agreement. That is it — we handle the campaign build, the skill mechanic, the tracking and the winner story from there.',
  },
];

// ---------------------------------------------------------------------------
// "Why partner with us" value section + sales hooks.
// Persuasion stance (applied ethically): jobs-to-be-done (lead with the
// sponsor's outcome), specific contrast vs forgettable ads, social proof,
// loss aversion, reciprocity + risk-reversal (flexible funding — we share the
// risk, fully funding the first few campaigns then open to negotiation, never
// locked in). Compliance-safe: skill-based only, consent-first, no
// guaranteed-win language (enforced by src/content/site.test.ts).
// ---------------------------------------------------------------------------

export type ValueProp = {
  icon: IconName;
  title: string;
  /** The job the sponsor is hiring us to do — their outcome, in their words. */
  outcome: string;
  /** Specific contrast: what this replaces or beats. */
  proof: string;
};

export const WHY_PARTNER = {
  eyebrow: 'What you actually get',
  headline: 'Spend less on ads people forget. Own a campaign they compete to win.',
  subcopy:
    'A sponsored skill-based competition turns one product into measurable foot traffic, opt-in leads and a brand moment with real social proof — all designed, run and reported by us. You bring the product and your logo; we do the rest.',
  /** v1-11-1 studio art reused tastefully. */
  image: '/studio/partnership/dealership-foot-traffic.webp',
  imageAlt: 'A sponsored product at the centre of a co-branded skill-based competition campaign',
  props: [
    {
      icon: 'traffic',
      title: 'Foot traffic you can measure',
      outcome: 'Get real people through the door, not just impressions on a dashboard.',
      proof: 'QR codes on the product, desk and banners route scans straight to your floor — and every one is attributed to your campaign.',
    },
    {
      icon: 'leads',
      title: 'Leads that opted in to hear from you',
      outcome: 'Build a pipeline of people who actively chose to be contacted about your product.',
      proof: 'Recorded, campaign-specific consent on every lead — not a scraped or rented list you can never fully trust.',
    },
    {
      icon: 'video',
      title: 'Winner content that sells for months',
      outcome: 'Walk away with shareable proof that your brand makes good things happen.',
      proof: 'A captured prize handover and winner announcement become social posts, case-study material and ongoing brand buzz.',
    },
    {
      icon: 'sparkles',
      title: 'Done-for-you, start to finish',
      outcome: 'Run a full marketing campaign without adding a single task to your team.',
      proof: 'We design the page, build the skill mechanic, set up tracking and produce the content. You co-brand and approve.',
    },
    {
      icon: 'shield',
      title: 'Flexible funding — we share the risk',
      outcome: "Prove the model before you commit, with no hard lock-in.",
      proof: "We'll fully fund the first few campaigns while we build trust, then every campaign is open to negotiation — some sponsored by you, some funded by us, whatever fits. Never 'on your own after run one.'",
    },
    {
      icon: 'chart',
      title: 'A report you can take upstairs',
      outcome: 'Show your board exactly what the spend returned.',
      proof: 'Scans, clicks, entries and opt-in leads in one clear campaign report — measurable outcomes, not vanity reach.',
    },
  ] satisfies ValueProp[],
  cta: CTAS.sponsorPrize,
  secondaryCta: CTAS.bookCall,
};

// Sales hooks — a scannable band of benefit + proof statements written to make
// a busy exec keep reading. Each pairs a vivid claim with concrete substance.
export type SalesHook = { hook: string; support: string };

export const SALES_HOOKS: SalesHook[] = [
  {
    hook: 'Turn one prize into measurable foot traffic, opt-in leads and brand buzz.',
    support: 'A single sponsored product, working across your store, your site and social — all tracked.',
  },
  {
    hook: 'Own a competition people compete to win — not another ad they scroll past.',
    support: 'Skill-based engagement holds attention far longer than a banner ever could.',
  },
  {
    hook: 'Flexible funding — we share the risk, never locked in.',
    support: "We fully fund the first few campaigns, then every run is open to negotiation — some on us, some sponsored by you, whatever fits.",
  },
  {
    hook: 'Real, consented leads. Measurable, not vanity.',
    support: 'Every contact opted in; every result lands in a report you can act on.',
  },
];

// ---------------------------------------------------------------------------
// "After you apply" onboarding stepper — removes the #1 reason B2B prospects
// don't apply: uncertainty about what they're signing up for. Onboarding-CRO:
// lower activation energy (step 1 is tiny + no commitment), set honest
// expectations (a concrete review SLA), and reverse risk (done-for-you build,
// flexible funding where we share the risk — never locked in). Distinct from
// HOW_IT_WORKS, which explains the campaign mechanic — this maps the prospect's
// own journey from form to inbox.
// Compliance-safe: skill-based only, consent-first, no guaranteed-win language
// (enforced by src/content/site.test.ts).
//
// SLA NOTE: "within 2 business days" is the review turnaround. No existing site
// copy pins a different number (the FAQ + Confirmation say "review and get back
// to you" without a figure), so this makes the vague promise concrete and
// honest for a B2B partnerships team — it does not contradict anything shipped.
// ---------------------------------------------------------------------------

export type ApplyStep = {
  /** Display number (1–6). */
  step: string;
  icon: IconName;
  title: string;
  /** What happens, in the prospect's terms — expectation-setting. */
  description: string;
  /** Short friction-reducer pill: effort, timing or risk-reversal cue. */
  marker: string;
};

export const AFTER_YOU_APPLY = {
  eyebrow: 'After you apply',
  headline: 'What happens after you apply',
  subcopy:
    'No mystery, no obligation. Here is the exact path from a two-minute form to your brand in front of an engaged audience — most of it done for you.',
  reassurance:
    'You can stop at any step before signing. Applying costs nothing and commits you to nothing.',
  steps: [
    {
      step: '1',
      icon: 'check',
      title: 'You apply',
      description:
        'Fill in a short, two-minute form about your company and the product you want to sponsor. No commitment, no payment, no obligation.',
      marker: 'Takes ~2 minutes',
    },
    {
      step: '2',
      icon: 'clock',
      title: 'We review and reach out',
      description:
        'Our partnerships team checks brand fit, prize details and feasibility, then gets back to you — typically within 2 business days.',
      marker: 'Within 2 business days',
    },
    {
      step: '3',
      icon: 'handshake',
      title: 'You sign a simple agreement',
      description:
        'A short, plain-language partnership agreement sets out the prize, the co-branding and the consent rules. No legalese marathon.',
      marker: 'Plain-language, short-form',
    },
    {
      step: '4',
      icon: 'sparkles',
      title: 'We design and build it',
      description:
        'We create your co-branded, skill-based competition end to end — the page, the creative, the skill mechanic and the tracking. Done for you.',
      marker: 'Done-for-you build',
    },
    {
      step: '5',
      icon: 'megaphone',
      title: 'It goes live',
      description:
        'Your campaign launches and your brand goes in front of an engaged audience who actively compete to win — not an ad they scroll past.',
      marker: 'Your brand, front and centre',
    },
    {
      step: '6',
      icon: 'chart',
      title: 'Leads and a report land in your inbox',
      description:
        'Opt-in leads with recorded, campaign-specific consent and a board-ready report on scans, clicks and entries arrive once the campaign runs.',
      marker: 'Consented leads + report',
    },
  ] satisfies ApplyStep[],
  cta: CTAS.sponsorPrize,
  secondaryCta: CTAS.bookCall,
};

export const FINAL_CTA = {
  headline: 'Ready to build your campaign?',
  subcopy:
    'We turn real products into measurable, skill-based prize campaigns — sponsor a prize, co-brand a campaign, drive traffic and capture opt-in leads.',
  primaryCta: CTAS.sponsorPrize,
  secondaryCta: CTAS.requestPack,
};

// Footer compliance note — skill-based, POPIA-aware, consent-first.
export const COMPLIANCE_NOTE =
  'All competitions are skill-based: the winner is decided solely by published skill criteria. Prizes are guaranteed to be given away to a winner decided by skill. Participant details are only ever shared with a partner where the participant has given clear, recorded, campaign-specific consent.';
